import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowDownLeft, ArrowLeft, ArrowUpRight, Hash, RefreshCw,
} from 'lucide-react';
import { formatDateTime, formatMoney } from '../lib/formatters';

// Kupon detay sayfalarının ortak parçaları.
//
// İki ekran kullanır: sitenin kendi kuponu (`SportsBetDetail`) ve hazır spor
// kitabının kuponu (`SportsbookBetDetail`). İkisi **aynı düzeni** çizmelidir —
// yönetici için tek bir soru vardır ("bu kupona ne oldu") ve ayrışan iki
// tasarım aynı bilgiyi iki ekranda iki ayrı yerde arattırırdı. Kaynaklar
// başkadır (biri `coupons` tablosundan, diğeri para hareketinden kurulur) ama
// gösterim tektir; bu yüzden kabuk burada, veri çözümlemesi sayfalarda durur.
//
// Yerleşim `admin/betDetail.css` içindedir; bileşenler `admin-*` sınıflarına
// yalnızca düğmelerde dokunur.

// Durum rozetinin rengi zaten `lib/sportsBets.js` ve `lib/hostedBets.js`
// içindeki `badge-*` sınıfındadır; ton oradan türetilir. İkinci bir eşleme
// yazılsaydı aynı kupon rozetinde yeşil, kartında gri görünebilirdi.
const TONE_BY_BADGE = {
  'badge-green': 'ok',
  'badge-red': 'bad',
  'badge-yellow': 'warn',
  'badge-gray': 'gray',
};

export const badgeTone = (cls) => TONE_BY_BADGE[cls] || 'gray';

// Sayfanın kökü. Kuponun durumu sayfanın rengidir: üst şerit, künye kartı ve
// rozetler aynı `--bdet-tone` değişkenini okur.
export const DetailPage = ({ tone = 'gray', children }) => (
  <div className={`bdet-page is-${tone}`}>{children}</div>
);

export const DetailTop = ({
  icon: Icon, title, backTo, backLabel = 'Listeye dön', tags, onRefresh,
  refreshing = false, actions,
}) => (
  <div className="bdet-top">
    <Link to={backTo} className="bdet-back">
      <ArrowLeft size={13} aria-hidden="true" /> {backLabel}
    </Link>
    <div className="bdet-top-title">
      {Icon && (
        <span className="bdet-top-icon">
          <Icon size={17} aria-hidden="true" />
        </span>
      )}
      <h1>{title}</h1>
    </div>
    {tags}
    <div className="bdet-top-actions">
      {actions}
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          className="admin-btn-secondary"
          disabled={refreshing}
        >
          <RefreshCw
            size={13}
            aria-hidden="true"
            className={refreshing ? 'animate-spin' : undefined}
          />
          Yenile
        </button>
      )}
    </div>
  </div>
);

export const Tag = ({ kind = '', children, title }) => (
  <span className={`bdet-tag${kind ? ` is-${kind}` : ''}`} title={title}>
    {children}
  </span>
);

export const Note = ({ tone = 'info', icon: Icon = AlertCircle, children, actions }) => (
  <div className={`bdet-note is-${tone}`} role={tone === 'bad' ? 'alert' : undefined}>
    <Icon size={15} aria-hidden="true" />
    <div className="bdet-note-body">{children}</div>
    {actions && <div className="bdet-note-actions">{actions}</div>}
  </div>
);

// Künye kartı: kuponun bütün rakamları tek yerde. Ayrı kutulara dağıtılan
// bahis/oran/ödeme/net dörtlüsü "ne kazandı" sorusunu dört yere baktırıyordu.
export const HeroCard = ({
  icon: Icon, statusLabel, statusNote, idLabel, idTitle, metrics = [],
}) => (
  <section className="bdet-hero">
    <header className="bdet-hero-head">
      {Icon && (
        <span className="bdet-hero-mark">
          <Icon size={19} aria-hidden="true" />
        </span>
      )}
      <div className="bdet-hero-text">
        <div className="bdet-hero-title">{statusLabel}</div>
        {statusNote && <div className="bdet-hero-sub">{statusNote}</div>}
      </div>
      {idLabel && (
        <span className="bdet-hero-id" title={idTitle || idLabel}>
          <Hash size={12} aria-hidden="true" />
          <span>{idLabel}</span>
        </span>
      )}
    </header>
    <div className="bdet-metrics">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={`bdet-metric${metric.tone ? ` is-${metric.tone}` : ''}`}
          title={metric.title}
        >
          <div className="bdet-metric-label">{metric.label}</div>
          <div className={`bdet-metric-value${metric.muted ? ' is-muted' : ''}`}>
            {metric.value}
          </div>
          {metric.hint && <div className="bdet-metric-hint">{metric.hint}</div>}
        </div>
      ))}
    </div>
  </section>
);

export const Panel = ({
  icon: Icon, title, subtitle, tone = '#38bdf8', flush = false, action, children,
}) => (
  <section className="bdet-panel">
    <header className="bdet-panel-head">
      {Icon && <Icon size={15} style={{ color: tone }} aria-hidden="true" />}
      <span className="bdet-panel-title">{title}</span>
      {subtitle && <span className="bdet-panel-sub">· {subtitle}</span>}
      {action}
    </header>
    <div className={`bdet-panel-body${flush ? ' is-flush' : ''}`}>{children}</div>
  </section>
);

export const Empty = ({ children }) => <p className="bdet-empty">{children}</p>;

// Bacak kartı. Alanların hepsi isteğe bağlıdır: sağlayıcıların gövde alan
// adları sözleşmede sabit değildir ve tanınmayan alan **yazılmaz** — uydurma
// bir maç adı, boş bir hücreden kötüdür.
export const LegCard = ({
  index, event, meta = [], pickLabel: pick, pickExtra, odd, oddExtra, score,
  resultLabel, resultTone = 'gray', source, note,
}) => (
  <article className={`bdet-leg is-${resultTone}`}>
    <div className="bdet-leg-top">
      {index !== undefined && <span className="bdet-leg-no">{index}</span>}
      <div className="bdet-leg-head">
        <div className="bdet-leg-event">{event}</div>
        {meta.filter(Boolean).length > 0 && (
          <div className="bdet-leg-meta">
            {meta.filter(Boolean).map((item, position) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={position}>{item}</span>
            ))}
          </div>
        )}
      </div>
      {resultLabel && (
        <span className={`bdet-leg-result is-${resultTone}`}>{resultLabel}</span>
      )}
    </div>
    <div className="bdet-leg-body">
      <div className="bdet-leg-cell is-grow">
        <span>Seçim</span>
        <strong>
          {pick || <span className="bdet-leg-none">—</span>}
          {pickExtra}
        </strong>
      </div>
      <div className="bdet-leg-cell">
        <span>Oran</span>
        <strong className="is-odd">
          {odd}
          {oddExtra}
        </strong>
      </div>
      <div className="bdet-leg-cell">
        <span>Skor</span>
        <strong className="is-soft">{score || '—'}</strong>
      </div>
      {source && (
        <div className="bdet-leg-cell">
          <span>Kaynak</span>
          <strong className="is-soft">{source}</strong>
        </div>
      )}
    </div>
    {note && <div className="bdet-leg-note">{note}</div>}
  </article>
);

// Para hareketleri. Yön tutarın işaretinden okunur: sıfır tutarlı satır
// (freebet bahsi, kaybeden kuponun sonuç bildirimi) nötr kalır — kasadan para
// çıkmamıştır ve yeşil/kırmızı bir ok yanlış bilgi verirdi.
export const FlowList = ({ rows = [], labels = {} }) => (
  <div className="bdet-flow">
    {rows.map((row) => {
      const amount = Number(row.amount || 0);
      const direction = amount > 0 ? 'in' : (amount < 0 ? 'out' : '');
      const Arrow = amount < 0 ? ArrowUpRight : ArrowDownLeft;
      return (
        <div className="bdet-flow-row" key={row.id || row.transaction_id}>
          <span className={`bdet-flow-mark${direction ? ` is-${direction}` : ''}`}>
            <Arrow size={13} aria-hidden="true" />
          </span>
          <div className="bdet-flow-body">
            <div className="bdet-flow-line">
              <span className="bdet-flow-name">
                {labels[row.role] || row.method || row.type}
              </span>
              <span className={`bdet-flow-amount${direction ? ` is-${direction}` : ''}`}>
                {formatMoney(amount)}
              </span>
              <span className="bdet-flow-when">{formatDateTime(row.created_at)}</span>
            </div>
            {(row.balance_after !== null && row.balance_after !== undefined) && (
              <div className="bdet-flow-meta">
                Kalan bakiye <b>{formatMoney(row.balance_after)}</b>
              </div>
            )}
            {row.note && <div className="bdet-flow-meta">{row.note}</div>}
          </div>
        </div>
      );
    })}
  </div>
);

export const Fact = ({ label, children }) => (
  <div className="bdet-fact">
    <span>{label}</span>
    <strong>{children}</strong>
  </div>
);

// Üye kartı iki sayfada da aynıdır; hazır kuponda üye silinmiş olabilir ve o
// zaman kart hiç çizilmez (çağıran taraf karar verir).
export const MemberCard = ({ user, children }) => (
  <>
    <div className="bdet-user">
      <span className="bdet-user-avatar" aria-hidden="true">
        {(user.username || '?').slice(0, 1)}
      </span>
      <div>
        <Link to={`/admin/users/${user.id}`} className="bdet-user-name">
          {user.username || '—'}
        </Link>
        <div className="bdet-user-sub">
          Kayıt {formatDateTime(user.created_at)}
        </div>
      </div>
      <Link
        to={`/admin/users/${user.id}`}
        className="admin-btn-secondary bdet-user-open"
      >
        Profili aç
      </Link>
    </div>
    <div className="bdet-facts">
      <Fact label="Ana bakiye">{formatMoney(user.balance)}</Fact>
      <Fact label="Bonus bakiyesi">{formatMoney(user.bonus_balance)}</Fact>
    </div>
    {children}
  </>
);
